import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../../api'
import { useResolver } from '../../../hooks'
import { JourneyStepType, Project, Subscription } from '../../../types'
import { Alert } from '../../../ui'
import RadioInput from '../../../ui/form/RadioInput'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { SubscriptionStepIcon } from '../../../ui/icons'
import './Subscription.css'

interface SubscriptionConfig {
    subscription_id?: number
    state: 'subscribed' | 'unsubscribed'
}

const useSubscriptions = (project: Project) => {
    const [subscriptions] = useResolver(useCallback(async () => {
        const { results } = await api.subscriptions.search(project.id, { limit: 9999, q: '' })
        return results.sort((a, b) => a.channel.localeCompare(b.channel) || Number(b.is_channel) - Number(a.is_channel) || a.name.localeCompare(b.name))
    }, [project]))
    return subscriptions ?? []
}

const needsConsent = (subscription?: Subscription) => subscription?.is_opt_in || subscription?.channel === 'text'

export const subscriptionStep: JourneyStepType<SubscriptionConfig> = {
    name: 'topic_update',
    icon: <SubscriptionStepIcon />,
    category: 'action',
    description: 'topic_update_desc',
    Describe({ project, value }) {
        const { t } = useTranslation()
        const subscription = useSubscriptions(project).find(s => s.id === value.subscription_id)
        if (!subscription) return <>{t('topic_update_empty')}</>
        return <>
            {t(value.state === 'unsubscribed' ? 'topic_update_describe_unsubscribe' : 'topic_update_describe_subscribe') + ' '}
            <strong>{subscription.name}</strong>
            {` (${t(subscription.channel)})`}
        </>
    },
    newData: async () => ({
        state: 'subscribed',
    }),
    Edit({ project, value, onChange, disabled }) {
        const { t } = useTranslation()
        const subscriptions = useSubscriptions(project)
        const selected = subscriptions.find(s => s.id === value.subscription_id)
        return <>
            <RadioInput
                label={t('topic_update_action')}
                options={[
                    { key: 'subscribed', label: t('topic_update_subscribe') },
                    { key: 'unsubscribed', label: t('topic_update_unsubscribe') },
                ]}
                value={value.state}
                onChange={state => onChange({ ...value, state })}
                disabled={disabled}
            />
            <SingleSelect
                label={t('topic_update_topic')}
                subtitle={t('topic_update_topic_desc')}
                options={subscriptions}
                value={value.subscription_id}
                onChange={subscription_id => onChange({ ...value, subscription_id })}
                toValue={x => x.id}
                getOptionSubtitle={x => t(x.channel)}
                searchable
                required
                disabled={disabled}
            />
            {value.state === 'subscribed' && needsConsent(selected) && (
                <div className="topic-update-consent">
                    <Alert variant="warn" size="small" title={t('topic_update_consent_title')}>
                        {t('topic_update_consent_body')}
                    </Alert>
                </div>
            )}
        </>
    },
}
